import { endOfMonth, startOfMonth } from 'date-fns';
import { Profile } from 'helpers/userProfile';
import { t } from 'i18next';
import { Moment } from 'moment-timezone';
import { tickets } from '~/pages/General';
import { EfficiencyCategory } from '~/providers/types/overview';
import { FilterContextType, SelectedOptions } from '../contexts/FilterContext';
import { UnitOption } from '../types';

export interface FilterStateRoute {
  clients?: string[];
  units?: string[];
  states?: string[];
  health?: number[];
  category?: string;
  cities?: string[];
}

type ParamType = 'agua' | 'unidades' | 'energia' | 'maquinas' | 'ambientes';

type AnalysisFilterOption<Value = number> = { value: Value, name: string };
type AnalysisSelectedOption<Value = number> = {
  label: string;
  values: AnalysisFilterOption<Value>[]
}

export function handleFilterStateRoute(
  clients: string[] | undefined,
  selectedOptions: FilterContextType['selectedOptions'],
  profile: Profile,
  health?: number[],
): FilterStateRoute {
  const filter: FilterStateRoute = {};

  if (selectedOptions.group?.units.length) {
    filter.units = selectedOptions.group.units.map((unit) => unit.toString());
  }

  if (selectedOptions.units?.length) {
    filter.units = selectedOptions.units.map((item) => item.value);
  }

  if (selectedOptions.state?.length) {
    filter.states = selectedOptions.state;
  }

  if (selectedOptions.cities?.length) {
    filter.cities = selectedOptions.cities.map((item) => item.value);
  }

  if (profile.manageMultipleClients && clients && clients.length > 0) {
    filter.clients = clients;
  }

  if (health?.length) {
    filter.health = health;
  }

  return filter;
}

export function handleOverviewStateFilters(
  filterState: FilterStateRoute | undefined,
  state: {
    selectedState: string[],
    selectedUnit: string[],
    selectedHealth?: number[],
    selectedClientFilter: string[],
    selectedCity: string[],
    startDate?: Date | Moment | null,
    endDate?: Date | Moment | null,
    energyEfficiency?: string[]
  },
  render: () => void,
  paramType?: string,
): void {
  // TODO - Adicionar outros cards de parâmetros
  if (!filterState || !isParamType(paramType)) return;

  if (filterState.clients) {
    state.selectedClientFilter = filterState.clients;
  }

  if (filterState.states?.length) {
    state.selectedState = filterState.states;
  }

  if (filterState.units?.length) {
    state.selectedUnit = filterState.units;
  }

  if (filterState.health?.length) {
    state.selectedHealth = filterState.health;
  }

  if (filterState.category) {
    state.energyEfficiency = [filterState.category];
  }

  if (filterState.cities) {
    state.selectedCity = filterState.cities;
  }

  if (paramType === 'energia') {
    state.endDate = endOfMonth(new Date());
    state.startDate = startOfMonth(new Date());
  }

  render();
}

function isParamType(type: string | undefined): type is ParamType {
  return !!type && (type === 'agua' || type === 'unidades' || type === 'energia' || type === 'maquinas' || type === 'ambientes');
}

export function handleFiltersAnalysis(filters: {
  clients: AnalysisFilterOption[],
  states: AnalysisFilterOption[],
  units: AnalysisFilterOption[],
  cities: AnalysisFilterOption<string>[],
  health?: AnalysisFilterOption[],
}, state: {
    selectedState: string[],
    selectedUnit: string[],
    selectedClientFilter: string[],
    selectedHealth?: number[],
    selectedCity: string[],
    energyEfficiency?: string[]
  }): {
  clients: AnalysisSelectedOption,
  states: AnalysisSelectedOption,
  units: AnalysisSelectedOption,
  cities: AnalysisSelectedOption<string>,
  health?: AnalysisSelectedOption,
} {
  const energyEfficiencyValues = state.energyEfficiency
    ? tickets
      .filter((ticket) => {
        const label = ticket.label.toUpperCase();
        return state.energyEfficiency?.includes(label);
      })
      .map((ticket) => {
        const label = ticket.label.toUpperCase();

        return {
          ...ticket,
          name: label,
          value: label,
        };
      })
    : undefined;

  return {
    clients: {
      label: t('cliente'),
      values: filters.clients.filter((item) => state.selectedClientFilter.includes(String(item.value))),
    },
    states: {
      label: t('estado'),
      values: filters.states.filter((item) => state.selectedState.includes(String(item.value))),
    },
    units: {
      label: t('unidade'),
      values: filters.units.filter((item) => state.selectedUnit.includes(String(item.value))),
    },
    cities: {
      label: t('cidade'),
      values: filters.cities.filter((item) => state.selectedCity.includes(item.value)),
    },
    ...(filters.health && {
      health: {
        label: t('saudeAtual'),
        values: filters.health.filter((item) => state.selectedHealth?.includes(item.value)),
      },
    }),
    ...(energyEfficiencyValues && {
      energyEfficiency: {
        label: 'Efic. Energética',
        values: energyEfficiencyValues,
      },
    }),
  };
}

export function handleEfficiencyRatingFilter(
  clients: string[] | undefined,
  selectedOptions: FilterContextType['selectedOptions'],
  profile: Profile,
  category: EfficiencyCategory,
): FilterStateRoute {
  const filter = handleFilterStateRoute(clients, selectedOptions, profile);

  filter.category = category;

  return filter;
}

export function filterUnitByOptions(
  options: SelectedOptions,
  unit: UnitOption,
  clients: string[] | undefined,
): boolean {
  const hasSelectedClient = clients && clients?.length > 0;
  const hasSelectedStates = options.state && options.state.length > 0;
  const hasSelectedCities = options.cities && options.cities.length > 0;
  const hasSelectedGroup = !!options.group;

  const conditions: boolean[] = [];

  if (hasSelectedClient) {
    conditions.push(clients.includes(String(unit.clientId)));
  }

  if (hasSelectedStates) {
    conditions.push(options.state?.includes(String(unit.stateId)) ?? false);
  }

  if (hasSelectedCities) {
    conditions.push(options.cities?.some((item) => item.value === unit.cityId) ?? false);
  }

  if (hasSelectedGroup) {
    conditions.push(options.group?.units.includes(Number(unit.value)) ?? false);
  }

  return conditions.every(Boolean);
}
